// The vendor admin navigation tree (IA §2.1), mapped to canonical IDs and gated by permission keys
// (API_CONTRACTS §4). A nav item the role can't access is hidden (IA §2.2 rules); the server still
// enforces every route. Areas from later phases render an honest "arrives in a later release" page.
import type { BrandName, IconName } from "@ady/ui";
import type { PlainKey } from "@ady/i18n";
import type { Membership, RolePreset } from "../api/types";

export type NavScope = "store" | "org";

export interface NavItem {
  id: string;
  label: PlainKey;
  /** Path under /{org}/{store}/ (scope store) or /{org}/ (scope org). */
  path: string;
  scope: NavScope;
  /** Any of these permissions shows the item. */
  anyOf: string[];
  canonical: string;
  phase: number;
}

export interface NavGroup {
  id: string;
  label: PlainKey;
  icon: IconName;
  /** A service's own mark shown in the sidebar instead of `icon` (Daraz; owner comment, 2026-11-03). */
  brand?: BrandName;
  items: NavItem[];
}

const s = (
  id: string,
  label: PlainKey,
  path: string,
  anyOf: string[],
  canonical: string,
  phase: number,
): NavItem => ({
  id,
  label,
  path,
  scope: "store",
  anyOf,
  canonical,
  phase,
});
const o = (
  id: string,
  label: PlainKey,
  path: string,
  anyOf: string[],
  canonical: string,
  phase: number,
): NavItem => ({
  id,
  label,
  path,
  scope: "org",
  anyOf,
  canonical,
  phase,
});

export const NAV: NavGroup[] = [
  {
    id: "today",
    label: "nav.groups.today",
    icon: "home",
    // Every member (e2e NAV_GATES "today: null"): it is everyone's landing page. Its sections gate
    // their own data as they ship (money and sales need finance/reports permissions).
    items: [s("today", "nav.groups.today", "today", [], "ANALYTICS-001", 4)],
  },
  {
    // CONV-001 Conversations (owner, 2026-10-24; D-055): one inbox for the website chat, Messenger,
    // Instagram, WhatsApp and TikTok, right under Home.
    id: "conversations",
    label: "nav.groups.conversations",
    icon: "chat",
    items: [
      s(
        "conversations",
        "nav.groups.conversations",
        "conversations",
        ["inbox.read"],
        "CONTENT-001",
        10,
      ),
    ],
  },
  // The Adyvance Wallet (D-064) pays for plans and services: named "Wallet", it sits under Sampark
  // (owner, 2026-10-27), no longer under Adyvance Services.
  {
    id: "adyWallet",
    label: "nav.groups.adyWallet",
    icon: "wallet",
    items: [s("adyWallet", "nav.groups.adyWallet", "wallet", ["billing.read"], "BILLING-001", 7)],
  },
  {
    id: "orders",
    label: "nav.groups.orders",
    icon: "orders",
    items: [
      s("orders", "nav.items.orders", "orders", ["orders.read"], "ORDER-001", 5),
      s(
        "unfinished",
        "nav.items.unfinished",
        "orders/unfinished",
        ["orders.read"],
        "CHECKOUT-001",
        5,
      ),
      s("returns", "nav.items.returns", "orders/returns", ["returns.manage"], "ORDER-001", 5),
      // Phase 6 (P6-B): RISK-04 COD confirmation calls.
      s("codChecks", "nav.items.codChecks", "orders/cod-checks", ["cod.verify"], "RISK-001", 6),
    ],
  },
  {
    // INTEGRATION-07 Daraz (owner, 2026-10-24; D-056): the shop's Daraz store, under Orders.
    id: "daraz",
    label: "nav.groups.daraz",
    icon: "bag",
    brand: "darazmarket",
    items: [s("daraz", "nav.groups.daraz", "daraz", ["orders.read"], "INTEGRATION-001", 10)],
  },
  {
    id: "sell",
    label: "nav.groups.sell",
    icon: "sell",
    items: [
      s("pos", "nav.items.pos", "sell", ["pos.sell"], "POS-001", 5),
      s("sales", "nav.items.sales", "sell/sales", ["pos.sell"], "POS-001", 5),
      s(
        "registers",
        "nav.items.registers",
        "sell/registers",
        ["pos.registers.manage", "pos.shift.manage"],
        "POS-001",
        6,
      ),
    ],
  },
  {
    id: "products",
    label: "nav.groups.products",
    icon: "box",
    items: [
      s("products", "nav.items.products", "products", ["products.read"], "PRODUCT-001", 5),
      s(
        "categories",
        "nav.items.categories",
        "products/categories",
        ["products.write"],
        "PRODUCT-001",
        5,
      ),
      s(
        "collections",
        "nav.items.collections",
        "products/collections",
        ["products.write"],
        "PRODUCT-001",
        5,
      ),
      s("brands", "nav.items.brands", "products/brands", ["products.write"], "PRODUCT-001", 5),
      s("inventory", "nav.items.inventory", "inventory", ["inventory.read"], "INVENTORY-001", 5),
      // Phase 6 (P6-D): INVENTORY-03 transfers, batches and the nightly stock check.
      s(
        "transfers",
        "nav.items.transfers",
        "inventory/transfers",
        ["inventory.read"],
        "INVENTORY-001",
        6,
      ),
      s("import", "nav.items.import", "products/import", ["products.bulk"], "IMPORT-001", 6),
      s("media", "nav.items.media", "media", ["media.read"], "MEDIA-001", 5),
    ],
  },
  {
    id: "customers",
    label: "nav.groups.customers",
    icon: "users",
    items: [
      s("customers", "nav.items.customers", "customers", ["customers.read"], "CUSTOMER-001", 5),
      s(
        "segments",
        "nav.items.segments",
        "customers/segments",
        ["customers.write"],
        "CUSTOMER-001",
        5,
      ),
      // Reviews and review requests (REVIEW-001) live with Customers (owner, 2026-10-21).
      s("reviews", "nav.items.reviews", "customers/reviews", ["reviews.moderate"], "REVIEW-001", 7),
      s("inbox", "nav.items.inbox", "customers/inbox", ["inbox.read"], "CONTENT-001", 5),
    ],
  },
  {
    id: "money",
    label: "nav.groups.money",
    icon: "money",
    items: [
      s("money", "nav.items.moneyOverview", "money", ["finance.read"], "FINANCE-001", 6),
      s("payments", "nav.items.payments", "money/payments", ["payments.read"], "PAYMENT-001", 6),
      s("cod", "nav.items.cod", "money/cod", ["finance.read"], "FINANCE-001", 6),
      // The FINANCE-01 ledger (cash book, accounts, journal) ships in Phase 6 (P6-A).
      s("accounts", "nav.items.accounts", "money/accounts", ["finance.read"], "FINANCE-001", 6),
      s("expenses", "nav.items.expenses", "money/expenses", ["expenses.manage"], "FINANCE-001", 7),
      s(
        "suppliers",
        "nav.items.suppliers",
        "money/suppliers",
        ["suppliers.manage"],
        "FINANCE-001",
        7,
      ),
      s(
        "statements",
        "nav.items.statements",
        "money/statements",
        ["reports.profit.read"],
        "FINANCE-001",
        7,
      ),
    ],
  },
  {
    id: "marketing",
    label: "nav.groups.marketing",
    icon: "megaphone",
    items: [
      s(
        "promotions",
        "nav.items.promotions",
        "marketing/promotions",
        ["promotions.manage"],
        "PROMOTION-001",
        7,
      ),
      s(
        "newsletter",
        "nav.items.newsletter",
        "marketing/newsletter",
        ["customers.read"],
        "CUSTOMER-001",
        10,
      ),
      s(
        "flashDeals",
        "nav.items.flashDeals",
        "marketing/flash-deals",
        ["promotions.manage"],
        "PROMOTION-001",
        10,
      ),
      // From the App store design (owner, 2026-10-21).
      s(
        "offerPopup",
        "nav.items.offerPopup",
        "marketing/offer-popup",
        ["promotions.manage"],
        "PROMOTION-001",
        10,
      ),
      s("links", "nav.items.links", "marketing/links", ["promotions.manage"], "CHECKOUT-001", 5),
      s(
        "automations",
        "nav.items.automations",
        "marketing/automations",
        ["automations.manage"],
        "AUTOMATION-001",
        7,
      ),
      s(
        "messages",
        "nav.items.messages",
        "marketing/messages",
        ["campaigns.send"],
        "NOTIFY-001",
        7,
      ),
      s(
        "affiliates",
        "nav.items.affiliates",
        "marketing/affiliates",
        ["affiliates.manage"],
        "AFFILIATE-001",
        7,
      ),
    ],
  },
  {
    id: "store",
    label: "nav.groups.store",
    icon: "store",
    items: [
      // Online store overview (owner, 2026-10-21): the sidebar's single link; every store page is a card.
      s(
        "storeHome",
        "nav.groups.store",
        "store",
        [
          "theme.edit",
          "content.edit",
          "store.domains.manage",
          "store.settings.update",
          "store.legal.update",
          "products.write",
          "products.bulk",
        ],
        "STORE-001",
        10,
      ),
      // Owner, 2026-10-22 (light merge): Blog is a tab of Pages and blog. The theme library sits on the
      // Online store page under the cards (owner, 2026-10-26). Old addresses redirect (modules/storefront.tsx).
      s("theme", "nav.items.theme", "store/theme", ["theme.edit"], "THEME-001", 5),
      s(
        "pageEditor",
        "nav.items.pageEditor",
        "store/page-editor",
        ["content.edit"],
        "THEME-001",
        10,
      ),
      s("codeEditor", "nav.items.codeEditor", "store/code-editor", ["theme.edit"], "THEME-001", 10),
      s("content", "nav.items.content", "store/content", ["content.edit"], "CONTENT-001", 5),
      s("menus", "nav.items.menus", "store/menus", ["theme.edit"], "THEME-001", 5),
      s("domains", "nav.items.domains", "store/domains", ["store.domains.manage"], "STORE-001", 5),
      s("seo", "nav.items.seo", "store/seo", ["store.settings.update"], "STORE-001", 5),
      s(
        "policies",
        "nav.items.policies",
        "store/policies",
        ["store.legal.update"],
        "CONTENT-001",
        5,
      ),
      // D-044 design-only screens (Zalient comparison, 2026-10-21): layout now, features later.
      s("branches", "nav.items.branches", "store/branches", ["content.edit"], "CONTENT-001", 10),
      s(
        "sellingPoints",
        "nav.items.sellingPoints",
        "store/selling-points",
        ["content.edit"],
        "CONTENT-001",
        10,
      ),
      // From the former App store page (owner, 2026-10-21).
      s("moveStore", "nav.items.moveStore", "store/move", ["products.bulk"], "IMPORT-001", 10),
    ],
  },
  {
    // Owner, 2026-11-01: the current theme and the theme gallery have their own sidebar link,
    // "Templates", right under Online store (they sat under the Online store cards before).
    id: "templates",
    label: "nav.groups.templates",
    icon: "palette",
    items: [
      s("templates", "nav.groups.templates", "store/templates", ["theme.edit"], "THEME-001", 5),
    ],
  },
  {
    id: "reports",
    label: "nav.groups.reports",
    icon: "chart",
    items: [
      s("reports", "nav.items.reports", "reports", ["reports.read"], "ANALYTICS-001", 7),
      s("traffic", "nav.items.traffic", "traffic", ["reports.read"], "ANALYTICS-001", 10),
    ],
  },
  {
    id: "apps",
    label: "nav.groups.apps",
    icon: "grid",
    items: [
      s(
        "appsHome",
        "nav.groups.apps",
        "apps",
        [
          "apps.manage",
          "integrations.manage",
          "webhooks.manage",
          "api_keys.manage",
          "shipping.settings",
          "payments.credentials.manage",
        ],
        "APPS-001",
        10,
      ),
      s(
        "integrations",
        "nav.items.integrations",
        "apps/integrations",
        ["integrations.manage"],
        "INTEGRATION-001",
        6,
      ),
      // Developer (owner, 2026-10-29; D-090): Developers and API & MCP merged into one Apps card with
      // one tab row (GroupBar): API keys (with test mode and the quick start) · MCP server ·
      // Webhooks. The "root AI assistant" and "AI shopping assistant" placeholders were removed
      // (root is set up in Sampark; the shopping assistant returns when it is built).
      s(
        "apiKeys",
        "nav.items.apiKeys",
        "apps/developers",
        ["api_keys.manage"],
        "INTEGRATION-001",
        6,
      ),
      s("mcp", "nav.items.mcp", "apps/developers/mcp", ["api_keys.manage"], "AI-001", 7),
      s(
        "webhooks",
        "nav.items.webhooks",
        "apps/developers/webhooks",
        ["webhooks.manage"],
        "INTEGRATION-001",
        6,
      ),
      // Phase 6 (P6-B): SHIPPING-05 courier accounts (credentials in the vault); under Apps
      // (owner, 2026-10-21).
      s(
        "couriers",
        "nav.items.couriers",
        "apps/couriers",
        ["shipping.settings"],
        "SHIPPING-001",
        6,
      ),
      // Delivery rules ship in Phase 5 (SHIPPING-01/02/06); a tab of Courier accounts (owner, 2026-10-21).
      s(
        "delivery",
        "nav.items.delivery",
        "apps/couriers/delivery",
        ["shipping.settings"],
        "SHIPPING-001",
        5,
      ),
      // Installation services: a third tab beside Courier accounts and Delivery (owner, 2026-10-30),
      // so the page has a tab row that says where you are; the address is unchanged.
      s(
        "installation",
        "nav.items.installation",
        "settings/delivery/installation",
        ["shipping.settings"],
        "SHIPPING-001",
        5,
      ),
      s(
        "paymentSettings",
        "nav.items.paymentSettings",
        "apps/payments",
        ["payments.credentials.manage"],
        "PAYMENT-001",
        6,
      ),
    ],
  },
  {
    id: "settings",
    label: "nav.groups.settings",
    icon: "gear",
    items: [
      s("settingsHome", "nav.groups.settings", "settings", [], "SETTINGS-001", 10),
      s(
        "storeProfile",
        "nav.items.storeProfile",
        "settings/store",
        ["store.settings.update"],
        "STORE-001",
        5,
      ),
      // Owner, 2026-10-22: moved here from the removed "More product details" tab.
      s(
        "digitalProducts",
        "nav.items.digitalProducts",
        "settings/digital-products",
        ["products.write"],
        "PRODUCT-001",
        7,
      ),
      s(
        "locations",
        "nav.items.locations",
        "settings/locations",
        ["locations.manage"],
        "INVENTORY-001",
        5,
      ),
      // Settings light merge (owner, 2026-10-22): Checkout, Order protection and Partial payment are
      // the tabs of one page; the old addresses open their tab (modules/settings.tsx).
      s(
        "checkout",
        "nav.items.checkout",
        "settings/checkout",
        ["settings.checkout", "risk.manage"],
        "CHECKOUT-001",
        5,
      ),
      s("loyalty", "nav.items.loyalty", "settings/loyalty", ["loyalty.manage"], "LOYALTY-001", 7),
      s("taxes", "nav.items.taxes", "settings/taxes", ["settings.tax"], "SETTINGS-001", 7),
      // Store email is a tab of Notifications; Markets a tab of Language and region (same merge).
      s(
        "notificationSettings",
        "nav.items.notificationSettings",
        "settings/notifications",
        ["settings.notifications"],
        "NOTIFY-001",
        7,
      ),
      s(
        "region",
        "nav.items.region",
        "settings/region",
        ["store.settings.update"],
        "SETTINGS-001",
        4,
      ),
      o("team", "nav.items.team", "settings/team", ["team.read"], "TEAM-001", 4),
      o("billing", "nav.items.billing", "settings/billing", ["billing.read"], "BILLING-001", 4),
      // Business verification and the Activity log are tabs of Organization.
      o(
        "organization",
        "nav.items.organization",
        "settings/organization",
        ["org.update", "audit.read"],
        "ORG-001",
        4,
      ),
    ],
  },
  // Help centre (owner, 2026-10-23): its own sidebar link under Settings, not a Settings card. The
  // address stays settings/help so old links keep working.
  {
    id: "help",
    label: "nav.groups.help",
    icon: "lifebuoy",
    items: [s("help", "nav.items.help", "settings/help", [], "SUPPORT-001", 8)],
  },
  // Adyvance Services (D-046, owner 2026-10-21): the company's own services, under their own heading.
  {
    id: "importMarketplace",
    label: "nav.groups.importMarketplace",
    icon: "ship",
    items: [
      s(
        "importMarketplace",
        "nav.groups.importMarketplace",
        "services/import-marketplace",
        [],
        "SERVICES-001",
        10,
      ),
    ],
  },
  {
    id: "smsService",
    label: "nav.groups.smsService",
    icon: "chat",
    items: [s("smsService", "nav.groups.smsService", "services/sms", [], "NOTIFY-001", 10)],
  },
  {
    id: "digitalMarketing",
    label: "nav.groups.digitalMarketing",
    icon: "target",
    items: [
      s(
        "digitalMarketing",
        "nav.groups.digitalMarketing",
        "services/digital-marketing",
        [],
        "SERVICES-001",
        10,
      ),
    ],
  },
  {
    id: "designContent",
    label: "nav.groups.designContent",
    icon: "image",
    items: [
      s(
        "designContent",
        "nav.groups.designContent",
        "services/design-content",
        [],
        "SERVICES-001",
        10,
      ),
    ],
  },
  {
    id: "growthStrategy",
    label: "nav.groups.growthStrategy",
    icon: "rocket",
    items: [
      s(
        "growthStrategy",
        "nav.groups.growthStrategy",
        "services/growth-strategy",
        [],
        "SERVICES-001",
        10,
      ),
    ],
  },
  {
    id: "courses",
    label: "nav.groups.courses",
    icon: "graduation",
    items: [s("courses", "nav.groups.courses", "services/courses", [], "SERVICES-001", 10)],
  },
];

/** An empty `anyOf` means every active member of the org. */
export function can(m: Membership | undefined, anyOf: string[]): boolean {
  if (!m) return false;
  return anyOf.length === 0 || anyOf.some((p) => m.permissions.includes(p));
}

/** The stable nav key used in test IDs (`nav-<key>`): the item's path with "/" → "-". */
export function navKey(item: NavItem): string {
  return item.path.replaceAll("/", "-");
}

/** The tree with every item the membership can't access removed (and empty groups dropped). */
export function visibleNav(m: Membership | undefined): NavGroup[] {
  return NAV.map((g) => ({ ...g, items: g.items.filter((i) => can(m, i.anyOf)) })).filter(
    (g) => g.items.length > 0,
  );
}

export function hrefFor(item: NavItem, org: string, store: string): string {
  return item.scope === "org" ? `/${org}/${item.path}` : `/${org}/${store}/${item.path}`;
}

export function findItem(id: string): NavItem | undefined {
  return NAV.flatMap((g) => g.items).find((i) => i.id === id);
}

/** Finds the nav item for a path below /{org}/{store}/ or /{org}/. */
export function itemForPath(scope: NavScope, path: string): NavItem | undefined {
  return NAV.flatMap((g) => g.items).find((i) => i.scope === scope && i.path === path);
}

/** Mobile bottom bar per role preset (IA §2.2); the last slot is always More. */
const BOTTOM_BY_PRESET: Partial<Record<RolePreset, string[]>> = {
  owner: ["today", "orders", "pos", "products"],
  manager: ["today", "orders", "pos", "products"],
  order_manager: ["today", "orders", "returns", "customers"],
  cashier: ["pos", "sales", "customers", "registers"],
  viewer: ["today", "reports", "money"],
};

export function bottomBarItems(m: Membership | undefined): NavItem[] {
  const preset = m?.role.preset;
  const wanted = (preset && BOTTOM_BY_PRESET[preset]) ?? [];
  const visible = visibleNav(m).flatMap((g) => g.items);
  const picked = wanted
    .map((id) => visible.find((i) => i.id === id))
    .filter((i): i is NavItem => Boolean(i));
  // Custom roles (or presets missing a slot): fill with the first visible group entries.
  for (const g of visibleNav(m)) {
    if (picked.length >= 4) break;
    const first = g.items[0];
    if (first && !picked.includes(first)) picked.push(first);
  }
  return picked.slice(0, 4);
}

/** One icon per area (D-038): sidebar rows, page headers, the bottom bar, menus and Coming soon. */
export const ICON_FOR_ITEM: Record<string, IconName> = {
  today: "home",
  conversations: "chat",
  orders: "orders",
  daraz: "bag",
  unfinished: "cart",
  returns: "returnArrow",
  invoices: "receipt",
  codChecks: "phoneCheck",
  pos: "sell",
  sales: "receipt",
  registers: "drawer",
  products: "box",
  categories: "grid",
  collections: "layers",
  brands: "tag",
  inventory: "archive",
  transfers: "swap",
  reviews: "star",
  import: "upload",
  media: "image",
  customers: "users",
  segments: "pie",
  loyalty: "gift",
  offerPopup: "megaphone",
  inbox: "chat",
  money: "money",
  payments: "card",
  cod: "coins",
  accounts: "bank",
  expenses: "wallet",
  suppliers: "factory",
  statements: "file",
  promotions: "percent",
  links: "link",
  automations: "zap",
  messages: "send",
  affiliates: "share",
  storeHome: "store",
  appsHome: "grid",
  settingsHome: "gear",
  theme: "palette",
  content: "pages",
  menus: "menu",
  domains: "globe",
  seo: "trending",
  policies: "shield",
  reports: "chart",
  moveStore: "upload",
  integrations: "plug",
  developers: "terminal",
  storeProfile: "store",
  locations: "mapPin",
  paymentSettings: "card",
  delivery: "truck",
  couriers: "route",
  checkout: "shieldCheck",
  taxes: "scale",
  notificationSettings: "bell",
  region: "flag",
  adyWallet: "wallet",
  importMarketplace: "ship",
  digitalMarketing: "target",
  designContent: "image",
  growthStrategy: "rocket",
  courses: "graduation",
  smsService: "chat",
  mcp: "plug",
  apiKeys: "key",
  webhooks: "send",
  digitalProducts: "key",
  pageEditor: "layout",
  codeEditor: "code",
  traffic: "eye",
  newsletter: "mail",
  flashDeals: "timer",
  branches: "building",
  sellingPoints: "badge",
  installation: "wrench",
  help: "lifebuoy",
  team: "users",
  billing: "receipt",
  activity: "pulse",
  organization: "building",
};

/** The icon for a nav item, falling back to its group's icon. */
export function iconFor(item: NavItem): IconName {
  return ICON_FOR_ITEM[item.id] ?? NAV.find((g) => g.items.includes(item))?.icon ?? "grid";
}

/** The service mark a nav item shows instead of its icon (Daraz), from its group. */
export function brandFor(item: NavItem): BrandName | undefined {
  return NAV.find((g) => g.items.includes(item))?.brand;
}

/** The nav item the current path belongs to (the longest matching path wins). */
export function itemForPathname(
  pathname: string,
  orgSlug: string,
  storeSlug: string,
): NavItem | undefined {
  let best: NavItem | undefined;
  let bestLen = -1;
  for (const item of NAV.flatMap((g) => g.items)) {
    const href = hrefFor(item, orgSlug, storeSlug);
    if ((pathname === href || pathname.startsWith(`${href}/`)) && href.length > bestLen) {
      best = item;
      bestLen = href.length;
    }
  }
  return best;
}
